import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { maskEmail, maskPhone, maskName, maskDOB, maskPatientPII } from '@/utils/hipaaUtils';

interface PatientData {
  id?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  dob?: string | null;
  address?: string | null;
}

interface DisplayData {
  displayName: string;
  displayFirstName: string;
  displayLastName: string;
  displayEmail: string;
  displayPhone: string;
  displayDOB: string;
  isMasked: boolean;
}

interface UseHIPAAMaskingResult {
  /** Whether the current user role requires masking (before assignment check) */
  userRequiresMasking: boolean;
  /** Set of patient IDs that are assigned to the current supervisor */
  assignedPatientIds: Set<string>;
  /** Loading state for assignments */
  loadingAssignments: boolean;
  /** Whether the current user is a supervisor */
  isSupervisor: boolean;
  /** Check if a specific patient should be masked */
  shouldMaskPatient: (patientId: string) => boolean;
  /** Get display data for a patient with appropriate masking */
  getDisplayData: (patient: PatientData) => DisplayData;
  /** Mask functions for individual fields */
  maskEmail: (email: string | null | undefined, patientId?: string) => string;
  maskPhone: (phone: string | null | undefined, patientId?: string) => string;
  maskName: (firstName: string | null | undefined, lastName: string | null | undefined, patientId?: string) => string;
  maskDOB: (dob: string | null | undefined, patientId?: string) => string;
}

/**
 * Hook for HIPAA-compliant PII masking in patient-facing displays
 * 
 * Access control:
 * - Super Admins: Full access to all patient data
 * - Agency Admins: Full access to their agency's patient data
 * - Supervisors: Full access ONLY to assigned patients, masked for others
 * - Other roles: Masked view for HIPAA compliance
 */
export function useHIPAAMasking(): UseHIPAAMaskingResult {
  const { user } = useAdminAuth();
  const [assignedPatientIds, setAssignedPatientIds] = useState<Set<string>>(new Set());
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);

  // Check if current user is a supervisor (via user_roles table)
  useEffect(() => {
    const checkSupervisorRole = async () => {
      if (!user) {
        setIsSupervisor(false);
        return;
      }

      // Super admins and agency admins are already covered
      if (user.role === 'super_admin' || user.role === 'agency_admin') {
        setIsSupervisor(false);
        return;
      }

      // Check user_roles table for supervisor role
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'supervisor')
          .maybeSingle();

        if (!error && data) {
          setIsSupervisor(true);
        } else {
          setIsSupervisor(false);
        }
      } catch (error) {
        console.error('Error checking supervisor role:', error);
        setIsSupervisor(false);
      }
    };

    checkSupervisorRole();
  }, [user]);

  // Determine if the current user's role requires masking
  const userRequiresMasking = useMemo(() => {
    if (!user) return true;
    
    // Super admins see full data for everyone
    if (user.role === 'super_admin') return false;
    
    // Agency admins see full data for their agency
    if (user.role === 'agency_admin') return false;
    
    // Supervisors need assignment-based masking
    if (isSupervisor) return true;
    
    // Default: don't mask for admin users
    return false;
  }, [user, isSupervisor]);

  // Load assigned patient IDs for supervisors
  useEffect(() => {
    const loadAssignments = async () => {
      if (!user || !isSupervisor) {
        setAssignedPatientIds(new Set());
        return;
      }

      setLoadingAssignments(true);
      try {
        const { data, error } = await supabase
          .from('patient_assignments')
          .select('patient_id')
          .eq('staff_user_id', user.id)
          .is('revoked_at', null);

        if (error) {
          console.error('Error loading patient assignments:', error);
          setAssignedPatientIds(new Set());
        } else {
          const ids = new Set(data?.map(a => a.patient_id) || []);
          setAssignedPatientIds(ids);
        }
      } catch (error) {
        console.error('Error loading patient assignments:', error);
        setAssignedPatientIds(new Set());
      } finally {
        setLoadingAssignments(false);
      }
    };

    loadAssignments();
  }, [user, isSupervisor]);

  // Check if a specific patient should be masked
  const shouldMaskPatient = useCallback((patientId: string): boolean => {
    if (!user) return true;
    
    // Super admins and agency admins see full data
    if (user.role === 'super_admin' || user.role === 'agency_admin') {
      return false;
    }
    
    // Supervisors: only assigned patients get full access
    if (isSupervisor) {
      return !assignedPatientIds.has(patientId);
    }
    
    // Default: don't mask for admin users
    return false;
  }, [user, isSupervisor, assignedPatientIds]);

  // Mask functions with optional patient ID
  const wrappedMaskEmail = useCallback((email: string | null | undefined, patientId?: string): string => {
    const shouldMask = patientId ? shouldMaskPatient(patientId) : userRequiresMasking;
    return shouldMask ? maskEmail(email) : (email || '');
  }, [shouldMaskPatient, userRequiresMasking]);

  const wrappedMaskPhone = useCallback((phone: string | null | undefined, patientId?: string): string => {
    const shouldMask = patientId ? shouldMaskPatient(patientId) : userRequiresMasking;
    return shouldMask ? maskPhone(phone) : (phone || '');
  }, [shouldMaskPatient, userRequiresMasking]);

  const wrappedMaskName = useCallback((firstName: string | null | undefined, lastName: string | null | undefined, patientId?: string): string => {
    const shouldMask = patientId ? shouldMaskPatient(patientId) : userRequiresMasking;
    return shouldMask ? maskName(firstName, lastName) : `${firstName || ''} ${lastName || ''}`.trim();
  }, [shouldMaskPatient, userRequiresMasking]);

  const wrappedMaskDOB = useCallback((dob: string | null | undefined, patientId?: string): string => {
    const shouldMask = patientId ? shouldMaskPatient(patientId) : userRequiresMasking;
    return shouldMask ? maskDOB(dob) : (dob || '');
  }, [shouldMaskPatient, userRequiresMasking]);

  // Get display data with masking based on patient ID
  const getDisplayData = useCallback((patient: PatientData): DisplayData => {
    const patientId = patient.id || '';
    const shouldMask = patientId ? shouldMaskPatient(patientId) : userRequiresMasking;

    if (shouldMask) {
      const masked = maskPatientPII(patient);
      return {
        displayName: masked.displayName,
        displayFirstName: patient.first_name ? `${patient.first_name.charAt(0)}***` : '***',
        displayLastName: patient.last_name ? `${patient.last_name.charAt(0)}***` : '***',
        displayEmail: masked.displayEmail,
        displayPhone: masked.displayPhone,
        displayDOB: masked.displayDOB,
        isMasked: true,
      };
    }

    return {
      displayName: `${patient.first_name || ''} ${patient.last_name || ''}`.trim(),
      displayFirstName: patient.first_name || '',
      displayLastName: patient.last_name || '',
      displayEmail: patient.email || '',
      displayPhone: patient.phone || '',
      displayDOB: patient.dob || '',
      isMasked: false,
    };
  }, [shouldMaskPatient, userRequiresMasking]);

  return {
    userRequiresMasking,
    assignedPatientIds,
    loadingAssignments,
    isSupervisor,
    shouldMaskPatient,
    getDisplayData,
    maskEmail: wrappedMaskEmail,
    maskPhone: wrappedMaskPhone,
    maskName: wrappedMaskName,
    maskDOB: wrappedMaskDOB,
  };
}
